import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Car, Fuel, Gauge, Calendar, ChevronLeft, ChevronRight, X, MapPin,
  CheckCircle, Shield, Star, DollarSign, Settings, Palette, Info,
  Zap, Award, FileText, ArrowLeftRight, Phone, MessageSquare, Heart
} from "lucide-react";

export default function VehicleDetailModal({ vehicle, open, onClose }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  if (!vehicle) return null;

  const images = vehicle.images || [
    "https://images.pexels.com/photos/170811/pexels-photo-170811.jpeg"
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const formatPrice = (price) => price ? `$${price.toLocaleString()}` : "N/A";
  const formatMileage = (miles) => miles !== undefined ? `${miles.toLocaleString()}` : "N/A";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] p-0 overflow-hidden bg-background">
        <div className="flex flex-col lg:flex-row h-full">
          {/* Image Gallery - Left Side */}
          <div className="lg:w-1/2 bg-slate-900 relative">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 left-4 z-20 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Favorite button */}
            <button
              onClick={() => setIsFavorite(!isFavorite)}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
            >
              <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
            </button>

            {/* Main Image */}
            <div className="relative h-[300px] lg:h-[400px] overflow-hidden">
              <img
                src={images[currentImageIndex]}
                alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 text-white text-sm">
                {currentImageIndex + 1} / {images.length}
              </div>
            </div>

            {/* Thumbnail Strip */}
            {images.length > 1 && (
              <div className="flex gap-2 p-4 overflow-x-auto bg-slate-950">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentImageIndex ? 'border-blue-500' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Highlights */}
            {vehicle.marketing_badges?.length > 0 && (
              <div className="p-4 bg-slate-950">
                <div className="flex flex-wrap gap-2">
                  {vehicle.marketing_badges.map((highlight, idx) => (
                    <Badge key={idx} className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {highlight}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Details - Right Side */}
          <div className="lg:w-1/2 flex flex-col h-[580px] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h2>
                  <p className="text-muted-foreground">{vehicle.trim}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-foreground">{formatPrice(vehicle.pricing.internet_price)}</div>
                  {vehicle.pricing.msrp && vehicle.pricing.msrp > vehicle.pricing.internet_price && (
                    <div className="flex items-center gap-2 justify-end">
                      <span className="text-sm text-muted-foreground line-through">MSRP {formatPrice(vehicle.pricing.msrp)}</span>
                      <Badge className="bg-emerald-500/20 text-emerald-500">Save {formatPrice(vehicle.pricing.msrp - vehicle.pricing.internet_price)}</Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-4 mt-6">
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <Gauge className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                  <div className="font-semibold">{formatMileage(vehicle.mileage.miles)}</div>
                  <div className="text-xs text-muted-foreground">Miles</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <Fuel className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                  <div className="font-semibold">{vehicle.mileage.mpg || "N/A"}</div>
                  <div className="text-xs text-muted-foreground">MPG</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <Settings className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                  <div className="font-semibold text-sm">{vehicle.performance.drivetrain}</div>
                  <div className="text-xs text-muted-foreground">Drivetrain</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <Palette className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                  <div className="font-semibold text-sm truncate">{vehicle.exterior.color?.split(' ')[0]}</div>
                  <div className="text-xs text-muted-foreground">Exterior</div>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <ScrollArea className="flex-1">
              <div className="p-6">
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="w-full grid grid-cols-3 mb-6">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="features">Features</TabsTrigger>
                    <TabsTrigger value="warranty">Warranty</TabsTrigger>
                  </TabsList>

                  {/* Overview Tab */}
                  <TabsContent value="overview" className="space-y-6">
                    {/* Basic Info */}
                    <div>
                      <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Car className="w-4 h-4 text-blue-500" /> Vehicle Details
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex justify-between p-2 rounded bg-muted/20">
                          <span className="text-muted-foreground">Stock #</span>
                          <span className="font-medium">{vehicle.identifiers.stock_number}</span>
                        </div>
                        <div className="flex justify-between p-2 rounded bg-muted/20">
                          <span className="text-muted-foreground">VIN</span>
                          <span className="font-medium font-mono text-xs">{vehicle.identifiers.vin?.slice(-8)}</span>
                        </div>
                        <div className="flex justify-between p-2 rounded bg-muted/20">
                          <span className="text-muted-foreground">Condition</span>
                          <span className="font-medium">{vehicle.condition.status}</span>
                        </div>
                        <div className="flex justify-between p-2 rounded bg-muted/20">
                          <span className="text-muted-foreground">Body Style</span>
                          <span className="font-medium">{vehicle.exterior.body_style}</span>
                        </div>
                      </div>
                    </div>

                    {/* Exterior & Interior */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Palette className="w-4 h-4 text-blue-500" /> Exterior
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Color</span>
                            <span>{vehicle.exterior.color}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Doors</span>
                            <span>{vehicle.exterior.doors}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Palette className="w-4 h-4 text-purple-500" /> Interior
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Color</span>
                            <span>{vehicle.interior.color}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Performance */}
                    <div>
                      <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-amber-500" /> Performance
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex justify-between p-2 rounded bg-muted/20">
                          <span className="text-muted-foreground">Engine</span>
                          <span className="font-medium text-right">{vehicle.performance.engine}</span>
                        </div>
                        <div className="flex justify-between p-2 rounded bg-muted/20">
                          <span className="text-muted-foreground">Transmission</span>
                          <span className="font-medium">{vehicle.performance.transmission}</span>
                        </div>
                        <div className="flex justify-between p-2 rounded bg-muted/20">
                          <span className="text-muted-foreground">Drivetrain</span>
                          <span className="font-medium">{vehicle.performance.drivetrain}</span>
                        </div>
                        <div className="flex justify-between p-2 rounded bg-muted/20">
                          <span className="text-muted-foreground">Fuel Type</span>
                          <span className="font-medium">{vehicle.performance.fuel_type}</span>
                        </div>
                      </div>
                    </div>

                    {/* Fuel Economy */}
                    <div>
                      <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Fuel className="w-4 h-4 text-emerald-500" /> Fuel Economy
                      </h3>
                      <div className="flex items-center gap-4 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                        <div className="text-center flex-1">
                          <div className="text-2xl font-bold text-emerald-500">{vehicle.fuel_economy.city}</div>
                          <div className="text-xs text-muted-foreground">City</div>
                        </div>
                        <div className="text-center flex-1 border-x border-border px-4">
                          <div className="text-2xl font-bold text-emerald-500">{vehicle.fuel_economy.highway}</div>
                          <div className="text-xs text-muted-foreground">Highway</div>
                        </div>
                        <div className="text-center flex-1">
                          <div className="text-2xl font-bold text-emerald-500">{vehicle.fuel_economy.combined}</div>
                          <div className="text-xs text-muted-foreground">Combined</div>
                        </div>
                      </div>
                    </div>

                    {/* Vehicle History */}
                    {vehicle.vehicle_history && (
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-500" /> Vehicle History
                        </h3>
                        <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <div className="text-xl font-bold text-foreground">{vehicle.vehicle_history.accidents}</div>
                              <div className="text-xs text-muted-foreground">Accidents</div>
                            </div>
                            <div>
                              <div className="text-xl font-bold text-foreground">{vehicle.vehicle_history.owners}</div>
                              <div className="text-xs text-muted-foreground">Owners</div>
                            </div>
                            <div>
                              <div className="text-xl font-bold text-foreground">{vehicle.vehicle_history.service_records}</div>
                              <div className="text-xs text-muted-foreground">Service Records</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* Features Tab */}
                  <TabsContent value="features" className="space-y-6">
                    {/* Features List */}
                    {vehicle.marketing_badges?.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Star className="w-4 h-4 text-amber-500" /> Key Features
                        </h3>
                        <div className="grid grid-cols-1 gap-2">
                          {vehicle.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2 rounded bg-muted/20">
                              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              <span className="text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Packages */}
                    {vehicle.packages?.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Award className="w-4 h-4 text-purple-500" /> Included Packages
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {vehicle.packages.map((pkg, idx) => (
                            <Badge key={idx} className="bg-purple-500/20 text-purple-400 border-purple-500/30 py-1.5 px-3">
                              {pkg}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* Warranty Tab */}
                  <TabsContent value="warranty" className="space-y-6">
                    {vehicle.warranty ? (
                      <div>
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-emerald-500" /> Warranty Coverage
                        </h3>
                        <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                          <div className="text-lg font-semibold text-emerald-500 mb-4">{vehicle.warranty.type}</div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 rounded bg-background">
                              <div className="text-sm text-muted-foreground">Comprehensive</div>
                              <div className="font-semibold">{vehicle.warranty.months} Months / {vehicle.warranty.miles?.toLocaleString()} Miles</div>
                            </div>
                            <div className="p-3 rounded bg-background">
                              <div className="text-sm text-muted-foreground">Powertrain</div>
                              <div className="font-semibold">{vehicle.warranty.powertrain_months} Months / {typeof vehicle.warranty.powertrain_miles === 'number' ? vehicle.warranty.powertrain_miles.toLocaleString() : vehicle.warranty.powertrain_miles} Miles</div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                            <span>150+ Point Inspection</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                            <span>24/7 Roadside Assistance</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                            <span>7-Day Money Back Guarantee</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Extended warranty options available at purchase</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </ScrollArea>

            {/* Action Buttons */}
            <div className="p-4 border-t border-border bg-muted/30">
              <div className="flex gap-3">
                <Button className="flex-1 btn-ai gap-2">
                  <Phone className="w-4 h-4" /> Contact Dealer
                </Button>
                <Button variant="outline" className="flex-1 gap-2">
                  <Calendar className="w-4 h-4" /> Schedule Test Drive
                </Button>
              </div>
              <p className="text-xs text-center text-muted-foreground mt-3">
                {vehicle.dealer_notes}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
